import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useShiftPlan } from '../../../context/ShiftPlanContext';
import { useTeam } from '../../../context/TeamContext';
import { TEAM_ROLES } from '../../../data/nurseAppModels';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';

const ShiftPlanListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { plans, loading, createPlanVersion, createPlanVersionWithSwaps } = useShiftPlan();
  const { currentTeam } = useTeam();
  const { currentUser } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [versionName, setVersionName] = useState('');
  const [creatingVersion, setCreatingVersion] = useState(false);
  const [approvedSwaps, setApprovedSwaps] = useState([]);
  const [loadingSwaps, setLoadingSwaps] = useState(false);
  const [selectedSwaps, setSelectedSwaps] = useState([]);
  const [expandedVersions, setExpandedVersions] = useState({});
  
  // Toggle versions timeline
  const toggleVersions = (planId) => {
    setExpandedVersions(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };

  // Check if we need to show create version modal from navigation state
  React.useEffect(() => {
    if (location.state?.createVersionForPlanId) {
      const plan = plans.find(p => p.planId === location.state.createVersionForPlanId);
      if (plan) {
        setSelectedPlan(plan);
        setShowVersionModal(true);
        // Clear state
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [plans, location.state, navigate, location.pathname]);

  // Check if user has permission
  const canCreatePlan = currentTeam && (
    currentTeam.userRole === TEAM_ROLES.LEADER || 
    currentTeam.userRole === TEAM_ROLES.DEPUTY_LEADER
  );

  // Check if user can approve (must be team creator/leaderId)
  const canApprove = React.useMemo(() => {
    if (!currentTeam || !currentUser) return false;
    return currentTeam.leaderId === currentUser.uid;
  }, [currentTeam, currentUser]);

  // Group plans by planId (all versions share the same planId)
  // Separate active and inactive versions
  const groupedPlans = React.useMemo(() => {
    const groups = {};

    // First pass: collect all plans by planId
    plans.forEach(plan => {
      const planId = plan.planId; // All versions share the same planId
      
      if (!groups[planId]) {
        groups[planId] = {
          original: null,
          versions: [],
          activeVersion: null,
          allVersions: []
        };
      }
      
      if (!plan.parentPlanId) {
        // Original plan (no parentPlanId)
        groups[planId].original = plan;
      } else {
        // Version plan (has parentPlanId)
        groups[planId].versions.push(plan);
      }
    });

    // Second pass: process each group
    Object.keys(groups).forEach(key => {
      const group = groups[key];
      
      // Collect all versions (original + versions)
      const allVersions = [];
      if (group.original) {
        allVersions.push(group.original);
      }
      allVersions.push(...group.versions);
      
      // Debug log
      console.log(`[ShiftPlanListPage] PlanId ${key}:`, {
        original: group.original ? `Version ${group.original.version} (ID: ${group.original.id})` : 'none',
        versions: group.versions.map(v => `Version ${v.version} (ID: ${v.id})`),
        total: allVersions.length,
        allVersions: allVersions.map(v => ({ version: v.version, id: v.id, planId: v.planId, parentPlanId: v.parentPlanId }))
      });
      
      // Sort by version number (descending) - latest first
      allVersions.sort((a, b) => (b.version || 0) - (a.version || 0));
      
      // Find active version (isActive = true, or latest approved version)
      const activeVersion = allVersions.find(v => v.isActive === true) || 
                           (allVersions.length > 0 && allVersions[0].status === 'APPROVED' ? allVersions[0] : null);
      
      if (activeVersion) {
        group.activeVersion = activeVersion;
      } else if (allVersions.length > 0) {
        // Fallback: use latest version if no active found
        group.activeVersion = allVersions[0];
      }
      
      // Store all versions for timeline (latest first)
      group.allVersions = allVersions;
    });

    // Return all groups that have at least one plan
    const result = [];
    
    Object.keys(groups).forEach(planId => {
      const group = groups[planId];
      
      // Skip if no plans at all
      if (!group.original && group.versions.length === 0) return;
      
      // If no original but we have versions, create a placeholder original
      if (!group.original && group.versions.length > 0) {
        const firstVersion = group.versions[0];
        group.original = {
          id: firstVersion.parentPlanId || planId,
          planId: planId,
          planNumber: firstVersion.planNumber,
          status: 'APPROVED',
          targetMonth: firstVersion.targetMonth
        };
      }
      
      // Only add if we have versions to show
      if (group.allVersions.length > 0) {
        result.push(group);
      }
    });

    // Sort by planNumber for consistent display
    result.sort((a, b) => {
      const aNum = a.original?.planNumber || '';
      const bNum = b.original?.planNumber || '';
      return bNum.localeCompare(aNum); // Descending
    });

    return result;
  }, [plans]);

  // Fetch approved swaps when plan is selected
  useEffect(() => {
    const fetchApprovedSwaps = async () => {
      if (!selectedPlan || !currentTeam) {
        setApprovedSwaps([]);
        setSelectedSwaps([]);
        return;
      }

      // Only fetch if plan is approved
      if (selectedPlan.status !== 'APPROVED') {
        setApprovedSwaps([]);
        setSelectedSwaps([]);
        return;
      }

      try {
        setLoadingSwaps(true);
        // Get the latest version of this plan (active version)
        const planDocId = selectedPlan.id;
        
        // Fetch swaps that are MEMBER_APPROVED for this plan
        const swapsRef = collection(db, 'shiftSwaps');
        const q = query(
          swapsRef,
          where('planId', '==', planDocId)
        );

        const snapshot = await getDocs(q);
        const swaps = snapshot.docs
          .map(docSnap => ({
            id: docSnap.id,
            ...docSnap.data()
          }))
          .filter(swap => swap.status === 'MEMBER_APPROVED');

        // Fetch user data for swaps
        const swapsWithUsers = await Promise.all(
          swaps.map(async (swap) => {
            const [requestedByUser, requestedToUser] = await Promise.all([
              getDoc(doc(db, 'users', swap.requestedBy)),
              getDoc(doc(db, 'users', swap.requestedTo))
            ]);

            return {
              ...swap,
              requestedByUser: requestedByUser.exists() ? requestedByUser.data() : null,
              requestedToUser: requestedToUser.exists() ? requestedToUser.data() : null
            };
          })
        );

        setApprovedSwaps(swapsWithUsers);
        // Auto-select all approved swaps
        setSelectedSwaps(swapsWithUsers.map(s => s.id));
      } catch (err) {
        console.error('Error fetching approved swaps:', err);
        setApprovedSwaps([]);
      } finally {
        setLoadingSwaps(false);
      }
    };

    if (showVersionModal && selectedPlan) {
      fetchApprovedSwaps();
    }
  }, [showVersionModal, selectedPlan, currentTeam]);

  // Handle create version
  const handleCreateVersion = async () => {
    if (!selectedPlan) return;

    setCreatingVersion(true);
    try {
      // Get the latest version plan document ID (not planId)
      const planDocId = selectedPlan.id;

      // If there are approved swaps, use createPlanVersionWithSwaps
      if (selectedSwaps.length > 0) {
        const result = await createPlanVersionWithSwaps(planDocId, selectedSwaps, versionName || null);
        
        if (result.success) {
          alert(`สร้าง Version ${result.version} สำเร็จ! รวม ${selectedSwaps.length} swaps แล้ว`);
          setShowVersionModal(false);
          setSelectedPlan(null);
          setVersionName('');
          setSelectedSwaps([]);
          setApprovedSwaps([]);
        } else {
          alert(`เกิดข้อผิดพลาด: ${result.error}`);
        }
      } else {
        // No swaps, create regular version
        const result = await createPlanVersion(selectedPlan.planId, versionName);
        
        if (result.success) {
          alert(`สร้าง Version ${result.plan.version} สำเร็จ!`);
          setShowVersionModal(false);
          setSelectedPlan(null);
          setVersionName('');
        } else {
          alert(`เกิดข้อผิดพลาด: ${result.error}`);
        }
      }
    } catch (err) {
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setCreatingVersion(false);
    }
  };

  // Get status label
  const getStatusLabel = (status) => {
    switch (status) {
      case 'DRAFT': return 'ร่าง';
      case 'SUBMITTED': return 'ส่งแล้ว';
      case 'APPROVED': return 'อนุมัติแล้ว';
      case 'REJECTED': return 'ปฏิเสธ';
      case 'CANCELLED': return 'ยกเลิก';
      default: return status;
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT': return 'bg-gray-100 text-gray-700';
      case 'SUBMITTED': return 'bg-yellow-100 text-yellow-700';
      case 'APPROVED': return 'bg-green-100 text-green-700';
      case 'REJECTED': return 'bg-red-100 text-red-700';
      case 'CANCELLED': return 'bg-gray-100 text-gray-500';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (!currentTeam) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 mb-4">กรุณาเลือกทีมก่อน</p>
          <button
            onClick={() => navigate('/nurse/teams')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            ไปเลือกทีม
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">แผนเวร</h1>
            <p className="text-sm text-gray-600 mt-1">
              จัดการแผนเวรทั้งหมด ({plans.length} แผน)
            </p>
          </div>
          {canCreatePlan && (
            <button
              onClick={() => navigate('/nurse/shifts/create')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              + สร้างแผนใหม่
            </button>
          )}
        </div>

        {/* Plans List */}
        {groupedPlans.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="text-6xl mb-4">📅</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              ยังไม่มีแผนเวร
            </h3>
            <p className="text-gray-600 mb-6">
              สร้างแผนเวรแรกของคุณเพื่อเริ่มต้นการวางแผน
            </p>
            {canCreatePlan && (
              <button
                onClick={() => navigate('/nurse/shifts/create')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                สร้างแผนใหม่
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {groupedPlans.map((group) => {
              const plan = group.original;
              const activeVersion = group.activeVersion || plan;
              const allVersions = group.allVersions || [];

              // Use planId as key for React
              const cardKey = plan?.planId || (allVersions.length > 0 ? allVersions[0].planId : 'unknown');

              return (
                <div key={cardKey} className="bg-white rounded-lg shadow-md p-6">
                  {/* Plan Header */}
                  <div className="border-b border-gray-200 pb-4 mb-4">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-gray-800">
                        📅 แผนเวร {plan?.planNumber || (allVersions.length > 0 ? allVersions[0].planNumber : 'N/A')}
                      </h3>
                      <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-700">
                        ID: {plan?.planId || (allVersions.length > 0 ? allVersions[0].planId : 'N/A')}
                      </span>
                      <span className={`px-2 py-1 text-xs rounded ${getStatusColor(plan?.status || 'APPROVED')}`}>
                        {getStatusLabel(plan?.status || 'APPROVED')}
                      </span>
                    </div>
                  </div>

                  {/* Active Version */}
                  {activeVersion && (
                    <div className="mb-4">
                      <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-lg">✅</span>
                          <h4 className="text-sm font-semibold text-gray-800">
                            Version {activeVersion.version} (ACTIVE)
                          </h4>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                            กำลังใช้งาน
                          </span>
                        </div>
                        
                        <div className="text-sm text-gray-700 space-y-1 mb-3">
                          <p>เลขแผน: <span className="font-medium">{activeVersion.planNumber}</span></p>
                          <p>เดือน: <span className="font-medium">{activeVersion.targetMonth}</span></p>
                          {activeVersion.createdAt && (
                            <p>สร้างเมื่อ: <span className="font-medium">
                              {activeVersion.createdAt?.toDate ? activeVersion.createdAt.toDate().toLocaleDateString('th-TH') :
                               activeVersion.createdAt?.seconds ? new Date(activeVersion.createdAt.seconds * 1000).toLocaleDateString('th-TH') :
                               'N/A'}
                            </span></p>
                          )}
                        </div>
                        
                        <div className="bg-white rounded-lg p-3 mb-3">
                          <p className="text-xs text-gray-600 font-medium mb-1">
                            💡 Version นี้กำลังใช้งานอยู่
                          </p>
                          <ul className="text-xs text-gray-600 space-y-0.5 ml-4">
                            <li>• สามารถ active งานได้</li>
                          </ul>
                        </div>
                        
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => navigate(`/nurse/shifts/plans/${activeVersion.id}/review`)}
                            className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                          >
                            ดู
                          </button>
                          {canCreatePlan && activeVersion.status === 'APPROVED' && (
                            <>
                              <button
                                onClick={() => navigate(`/nurse/shifts/plans/${activeVersion.id}/equipment`)}
                                className="px-4 py-2 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 transition"
                              >
                                🔧 จัดการอุปกรณ์
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedPlan(activeVersion);
                                  setShowVersionModal(true);
                                }}
                                className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition"
                              >
                                + สร้าง Version
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Inactive Versions Timeline (Collapsible) */}
                  {allVersions.filter(v => v.id !== activeVersion?.id).length > 0 && (
                    <div>
                      <button
                        onClick={() => toggleVersions(plan?.planId || cardKey)}
                        className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition mb-2"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-lg">📚</span>
                          <span className="text-sm font-medium text-gray-700">
                            ประวัติ Versions เก่า ({allVersions.filter(v => v.id !== activeVersion?.id).length} versions)
                          </span>
                        </div>
                        <svg
                          className={`w-5 h-5 text-gray-600 transition-transform ${
                            expandedVersions[plan?.planId || cardKey] ? 'transform rotate-180' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {expandedVersions[plan?.planId || cardKey] && (
                        <div className="space-y-2 pl-4 border-l-2 border-gray-300">
                          {allVersions
                            .filter(v => v.id !== activeVersion?.id)
                            .map((version, index) => (
                              <div
                                key={version.id}
                                className="bg-gray-50 border border-gray-300 rounded-lg p-3 opacity-75"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-sm">📄</span>
                                      <span className="text-sm font-medium text-gray-800">
                                        Version {version.version}
                                      </span>
                                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                                        INACTIVE
                                      </span>
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      <p>สร้างเมื่อ: <span className="font-medium">
                                        {version.createdAt?.toDate ? version.createdAt.toDate().toLocaleDateString('th-TH') :
                                         version.createdAt?.seconds ? new Date(version.createdAt.seconds * 1000).toLocaleDateString('th-TH') :
                                         'N/A'}
                                      </span></p>
                                      <p className="text-gray-500 mt-1 italic text-[10px]">
                                        ⚠️ Version เก่า - ดูได้เท่านั้น
                                      </p>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => navigate(`/nurse/shifts/plans/${version.id}/review`)}
                                    className="ml-3 px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 transition"
                                  >
                                    ดู
                                  </button>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Create Version Modal */}
        {showVersionModal && selectedPlan && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 my-8 max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                สร้าง Version ใหม่
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                สร้าง Version ใหม่ของแผน <span className="font-medium">{selectedPlan.planName || selectedPlan.planNumber}</span>
              </p>

              {/* Approved Swaps Section */}
              {selectedPlan.status === 'APPROVED' && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Swaps ที่ตอบรับแล้ว (MEMBER_APPROVED)
                    </label>
                    {approvedSwaps.length > 0 && (
                      <button
                        onClick={() => {
                          if (selectedSwaps.length === approvedSwaps.length) {
                            setSelectedSwaps([]);
                          } else {
                            setSelectedSwaps(approvedSwaps.map(s => s.id));
                          }
                        }}
                        className="text-xs text-blue-600 hover:text-blue-700"
                      >
                        {selectedSwaps.length === approvedSwaps.length ? 'ยกเลิกทั้งหมด' : 'เลือกทั้งหมด'}
                      </button>
                    )}
                  </div>

                  {loadingSwaps ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="text-sm text-gray-500 mt-2">กำลังโหลด swaps...</p>
                    </div>
                  ) : approvedSwaps.length === 0 ? (
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <p className="text-sm text-gray-500">ไม่มี swaps ที่ตอบรับแล้ว</p>
                      <p className="text-xs text-gray-400 mt-1">Version ใหม่จะถูกสร้างโดยไม่มี swaps</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                      {approvedSwaps.map((swap) => {
                        const isSelected = selectedSwaps.includes(swap.id);
                        const requesterName = swap.requestedByUser?.displayName || swap.requestedByName || 'Unknown';
                        const requestedToName = swap.requestedToUser?.displayName || swap.requestedToName || 'Unknown';
                        
                        return (
                          <div
                            key={swap.id}
                            className={`p-3 rounded-lg border-2 cursor-pointer transition ${
                              isSelected
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedSwaps(selectedSwaps.filter(id => id !== swap.id));
                              } else {
                                setSelectedSwaps([...selectedSwaps, swap.id]);
                              }
                            }}
                          >
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                className="mt-1"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-gray-800">
                                    {requesterName} ↔ {requestedToName}
                                  </span>
                                </div>
                                {swap.originalShift?.date && (
                                  <p className="text-xs text-gray-600">
                                    {swap.originalShift.date} - เวร{swap.originalShift.shiftType === 'MORNING' ? 'เช้า' : swap.originalShift.shiftType === 'AFTERNOON' ? 'บ่าย' : 'ดึก'}
                                    {swap.requestedShift?.date && (
                                      <> ↔ {swap.requestedShift.date} - เวร{swap.requestedShift.shiftType === 'MORNING' ? 'เช้า' : swap.requestedShift.shiftType === 'AFTERNOON' ? 'บ่าย' : 'ดึก'}</>
                                    )}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {approvedSwaps.length > 0 && (
                    <p className="text-xs text-gray-500 mt-2">
                      เลือก {selectedSwaps.length} / {approvedSwaps.length} swaps
                    </p>
                  )}
                </div>
              )}
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ชื่อ Version (ไม่บังคับ)
                </label>
                <input
                  type="text"
                  value={versionName}
                  onChange={(e) => setVersionName(e.target.value)}
                  placeholder={`${selectedPlan.planName || selectedPlan.planNumber} (Version ${(selectedPlan.version || 1) + 1})`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  ถ้าไม่กรอกจะใช้ชื่ออัตโนมัติ
                </p>
              </div>

              {selectedSwaps.length > 0 && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    ⚠️ Version ใหม่จะรวม {selectedSwaps.length} swaps ที่เลือกไว้
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowVersionModal(false);
                    setSelectedPlan(null);
                    setVersionName('');
                    setSelectedSwaps([]);
                    setApprovedSwaps([]);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                >
                  ยกเลิก
                </button>
                <button
                  onClick={handleCreateVersion}
                  disabled={creatingVersion}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingVersion ? 'กำลังสร้าง...' : `สร้าง Version${selectedSwaps.length > 0 ? ` (${selectedSwaps.length} swaps)` : ''}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShiftPlanListPage;

